import React from 'react';
import {
	Text,
	Box,
	Flex,
	Heading,
	Avatar,
	AvatarGroup,
	Spacer,
} from '@tempo/core';
import {useRouter} from 'next/router';
import type {GroupFragment} from '@graphql/generated';
import {useTranslation} from 'next-i18next';
import GroupActions from '@components/GroupActions';
import type {GroupSelected} from '@components/Modal/LeaveGroupModal';

type GroupItemProps = {
	group: GroupFragment;
	hasActions?: boolean;
};

const GroupItem = ({
	group: {id, name, members, isFamily},
	hasActions = false,
}: GroupItemProps) => {
	const router = useRouter();
	const {t} = useTranslation();
	const groupData: GroupSelected = React.useMemo(
		() => ({
			id,
			name,
			hasManyMembers: members.edges.length > 1,
		}),
		[id, members.edges.length, name],
	);

	return (
		<Box
			as={hasActions ? 'button' : 'div'}
			width="100%"
			borderRadius={8}
			mb="spacing.l"
			overflow="hidden"
			onClick={async () => {
				if (hasActions) {
					return router.push(`/group/${id}`);
				}
			}}
		>
			<Flex
				p="spacing.l"
				bg="background.neutral.secondary.default"
				justify="space-between"
				align="center"
			>
				<Heading
					as="h3"
					variant="heading.m"
					color="text.neutral.primary.default"
					isTruncated
				>
					{name}
				</Heading>

				{hasActions && <GroupActions groupData={groupData} />}
			</Flex>

			<Flex
				position="relative"
				p="spacing.l"
				justify="flex-start"
				alignItems="center"
				bg="background.neutral.tertiary.default"
			>
				<Box
					position="absolute"
					top="-5px"
					height="10px"
					left={0}
					bg="background.neutral.tertiary.default"
					width="100%"
					borderRadius="100% 100% 0 0"
				/>

				<AvatarGroup size="size.m" max={4} spacing={-2} fontSize="0.6rem">
					{members.edges.map(
						({node}) =>
							node && (
								<Avatar
									key={node.id}
									name={node.name}
									src={node.picture?.medium[0]}
								/>
							),
					)}
				</AvatarGroup>
				<Text
					ml="spacing.s"
					variant="body.s.default"
					color="text.neutral.secondary.default"
				>
					{t('musictogether_text_countXmembers_web', {
						sprintf: [members.edges.length],
						count: members.edges.length,
					})}
				</Text>
				<Spacer />
				{isFamily && (
					<Text
						bg="background.neutral.inverse.default"
						px="spacing.s"
						py="spacing.xs"
						variant="body.m.default"
						color="text.neutral.inverse.default"
						rounded="xs"
					>
						{t('deezerplans_title_family_web')}
					</Text>
				)}
			</Flex>
		</Box>
	);
};

export default GroupItem;
